import Version from './Version.js';
import DeviceConnection from './DeviceConnection.js';
import { ReconnectingWebSocket } from '@midulate/shared';
import _ from 'lodash';

// Firmware chunk size for OTA over WiFi. Larger than the USB COBS chunk (1 KB)
// because each HTTP chunk is a full request round-trip — bigger chunks mean
// fewer requests over a firmware transfer. Kept modest so the device's 4 KB
// receive buffer and the RP2350 heap stay comfortable during the upload.
const OTA_CHUNK_SIZE = 4096;

// ---------------------------------------------------------------------------
// HttpDeviceError — thrown by HttpDeviceConnection on non-OK HTTP responses.
//
// Callers can inspect .status to distinguish 401 (stale token — device
// rebooted) from other errors.  .isUnauthorized is a convenience shortcut.
// ---------------------------------------------------------------------------

export class HttpDeviceError extends Error {
  constructor(status) {
    super(`Device returned HTTP ${status}`);
    this.name = 'HttpDeviceError';
    this.status = status;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

// ---------------------------------------------------------------------------
// HttpDeviceConnection
//
// Connection flow (MDL-146):
//   1. HttpDeviceConnection.probe(baseUrl) — GET /auth → token, GET /version → version
//   2. DeviceConnection.connect('http', { baseUrl, token, version })
//
// Full HTTP command transport (run()) is wired up in MDL-147.
// ---------------------------------------------------------------------------

export default class HttpDeviceConnection extends DeviceConnection {
  // Probe a device at baseUrl.  Returns { token, version, uid } or throws.
  // All requests are sequential — the device HTTP server is single-threaded
  // and concurrent requests cause the second to be reset.
  static async probe(baseUrl, timeoutMs = 3000) {
    // Step 1 — fetch token
    const authRes = await fetch(`${baseUrl}/auth`, { signal: AbortSignal.timeout(timeoutMs) });
    if (!authRes.ok) throw new Error(`/auth returned ${authRes.status}`);
    const { token } = await authRes.json();

    // Step 2 — fetch uid (unauthenticated, but sequential for server compatibility)
    const uidRes = await fetch(`${baseUrl}/uid`, { signal: AbortSignal.timeout(timeoutMs) });
    if (!uidRes.ok) throw new Error(`/uid returned ${uidRes.status}`);
    const { uid } = await uidRes.json();

    // Step 3 — fetch version (auth required)
    const versionRes = await fetch(`${baseUrl}/version`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!versionRes.ok) throw new Error(`/version returned ${versionRes.status}`);
    const { version } = await versionRes.json();

    return { token, version, uid };
  }

  constructor({ baseUrl, token, version, uid = null }) {
    super();
    this.baseUrl         = baseUrl;
    this.token           = token;
    this.uid             = uid;
    this.firmwareVersion = Version.parse(version);
    this.connectionType  = 'http';
    this.connection      = { description: `WiFi ${baseUrl}` };

    // Debounced helpers — callers that invoke .cancel() (e.g. Patches.vue) won't throw.
    this.updateEffect       = _.debounce(this._updateEffect.bind(this), 500);
    this.updatePatchGlobals = _.debounce(this._updatePatchGlobals.bind(this), 500);

    this._ws                  = null;
    this._patchChangedCbs     = [];
    this._tempoChangedCbs     = [];
    this._midiCCBatchCbs      = [];
    this._parameterChangedCbs = [];
    this._effectMutedCbs      = [];
    this._midiLearnEndedCbs   = [];
    this._connectionStateCbs  = [];
  }

  // Register a callback to be invoked when the device pushes a patchChanged event.
  // cb(index) — index is 0-based patch number.
  onPatchChanged(cb) {
    this._patchChangedCbs.push(cb);
  }

  // Register a callback to be invoked when the device pushes a tempoChanged event.
  // cb(bpm, synced) — bpm is null when synced is false.
  onTempoChanged(cb) {
    this._tempoChangedCbs.push(cb);
  }

  // Register a callback for midiCCBatch events (WiFi/WebSocket only).
  // cb(messages) — messages is an array of { channel, cc, value } objects.
  // Display-only: never use to update the editor's canonical parameter model.
  onMidiCCBatch(cb) {
    this._midiCCBatchCbs.push(cb);
  }

  // Register a callback for parameterChanged events (a mapped incoming CC
  // was applied on the device). cb({ channel, cc, value }) — value is the
  // raw incoming CC value 0-127. Display-only: never use to update the
  // editor's canonical parameter model or dirty the save state.
  onParameterChanged(cb) {
    this._parameterChangedCbs.push(cb);
  }

  // Register a callback for effectMuted events (an effect's mute state was
  // toggled on the device by a mute mapping — externally driven).
  // cb({ effectIndex, muted }). Editor-initiated mutes are not echoed.
  onEffectMuted(cb) {
    this._effectMutedCbs.push(cb);
  }

  // Register a callback for midiLearnEnded events (device-side MIDI Learn).
  // cb({ reason, cc?, channel? }) — reason is 'captured' | 'cancelled' | 'timeout'.
  onMidiLearnEnded(cb) {
    this._midiLearnEndedCbs.push(cb);
  }

  // Arm / disarm device-side MIDI Learn capture. The captured CC arrives via
  // onMidiLearnEnded, not as the response to these calls.
  async startMidiLearn() {
    await this._fetch('POST', '/midi-learn/start');
  }

  async cancelMidiLearn() {
    await this._fetch('POST', '/midi-learn/cancel');
  }

  // Register a callback for WebSocket connection state changes.
  // cb(state) — state is 'connected' | 'reconnecting'.
  // Only relevant when the device is connected via WiFi.
  onConnectionStateChanged(cb) {
    this._connectionStateCbs.push(cb);
  }

  // Open a WebSocket connection to the device.  Safe to call multiple times —
  // closes any existing socket first.  The token is passed as a query param
  // because the browser WebSocket API does not support custom headers.
  openWebSocket() {
    if (this._ws) {
      this._ws.onConnectionStateChanged = null; // prevent stale state reaching callbacks
      this._ws.close();
      this._ws = null;
    }

    const ws = new ReconnectingWebSocket(({ reconnect }) => this._wsUrl({ reconnect }));
    this._ws = ws;

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      if (msg.event === 'patchChanged' && typeof msg.patch === 'number') {
        for (const cb of this._patchChangedCbs) cb(msg.patch);
      }
      if (msg.event === 'tempoChanged') {
        for (const cb of this._tempoChangedCbs) cb(msg.bpm, msg.synced);
      }
      if (msg.event === 'midiCCBatch' && Array.isArray(msg.messages)) {
        for (const cb of this._midiCCBatchCbs) cb(msg.messages);
      }
      if (msg.event === 'parameterChanged' && typeof msg.cc === 'number') {
        for (const cb of this._parameterChangedCbs) cb(msg);
      }
      if (msg.event === 'effectMuted' && typeof msg.effectIndex === 'number') {
        for (const cb of this._effectMutedCbs) cb(msg);
      }
      if (msg.event === 'midiLearnEnded' && typeof msg.reason === 'string') {
        for (const cb of this._midiLearnEndedCbs) cb(msg);
      }
    };

    ws.onConnectionStateChanged = (state) => {
      for (const cb of this._connectionStateCbs) cb(state);
    };

    ws.open();
  }

  // WebSocket URL for one connect attempt.
  //
  // The token rides in the URL, and the device re-mints it on every boot, so a
  // URL built once at connect time can never reconnect after a power cycle:
  // the device refuses the handshake with a 401, onopen never fires, and every
  // retry is refused the same way (MDL-384). Reconnect attempts therefore
  // fetch a fresh token first. /auth needs no token; if it fails the device is
  // still down and ReconnectingWebSocket backs off as for any failed attempt.
  //
  // Adopting the token fixes REST as well, since _fetch carries the same one.
  // The first attempt uses the token probe() just fetched, so connecting
  // costs no extra round trip.
  _wsUrl({ reconnect }) {
    return reconnect ? this._wsUrlWithFreshToken() : this._buildWsUrl();
  }

  _buildWsUrl() {
    return this.baseUrl.replace(/^http/, 'ws') + `/ws?token=${encodeURIComponent(this.token)}`;
  }

  async _wsUrlWithFreshToken() {
    const res = await fetch(`${this.baseUrl}/auth`, { signal: AbortSignal.timeout(3000) });
    if (!res.ok) throw new HttpDeviceError(res.status);
    const { token } = await res.json();
    if (token) this.token = token;
    return this._buildWsUrl();
  }

  // Authorised fetch helper.  Throws HttpDeviceError on non-OK responses;
  // lets native network errors (TypeError / DOMException) propagate as-is.
  async _fetch(method, path, body = null) {
    const opts = {
      method,
      headers: { Authorization: `Bearer ${this.token}` },
    };
    if (body !== null) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(`${this.baseUrl}${path}`, opts);
    if (!res.ok) {
      throw new HttpDeviceError(res.status);
    }
    return res;
  }

  // Maps USB-style command names to REST endpoints so that the transport-
  // agnostic base-class methods (download, uploadConfig, setPatch, …) work
  // identically over WiFi without any changes to the editor UI code.
  async run(command, data = null) {
    switch (command) {
      case 'VERSION':
        // Already known from probe() — no round-trip needed.
        return this.firmwareVersion.toString();

      case 'DOWNLOAD': {
        // Base-class download() calls JSON.parse(response) so we must return
        // a JSON string, not the already-parsed object.
        const res = await this._fetch('GET', '/config');
        return JSON.stringify(await res.json());
      }

      case 'UPLOAD_CONFIG':
        await this._fetch('POST', '/config', data);
        return 'OK';

      case 'SET_PATCH':
        await this._fetch('POST', '/active-patch', { uuid: data });
        return;

      case 'SET_EFFECTS':
        await this._fetch('POST', '/active-patch/effects', data);
        return;

      case 'SET_MUTED_EFFECTS':
        await this._fetch('POST', '/active-patch/muted-effects', data);
        return;

      case 'UPDATE_EFFECT':
        // data = { effectIndex, newEffect } — same shape as the USB command body.
        await this._fetch('PATCH', `/active-patch/effects/${data.effectIndex}`, data.newEffect);
        return;

      case 'UPDATE_PATCH_GLOBALS':
        await this._fetch('PATCH', '/active-patch/globals', data);
        return;

      case 'PREPARE_FIRMWARE_UPDATE':
        throw new Error('Firmware updates over WiFi are not supported — use USB (MDL-149)');

      default:
        throw new Error(`HttpDeviceConnection: unsupported command "${command}"`);
    }
  }

  async _updateEffect(effect) {
    await this.run('UPDATE_EFFECT', effect);
  }

  async _updatePatchGlobals(globals) {
    await this.run('UPDATE_PATCH_GLOBALS', globals);
  }

  async disconnect() {
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
  }

  // ---- REST-specific methods (no USB equivalents) ----

  // Returns current device state: { patch_index, bpm }.
  async getState() {
    const res = await this._fetch('GET', '/state');
    return res.json();
  }

  // Returns an array of patch summaries: [{ uuid, title }, …].
  async getPatches() {
    const res = await this._fetch('GET', '/patches');
    return res.json();
  }

  // Returns the full patch object for the given uuid.
  async getPatch(uuid) {
    const res = await this._fetch('GET', `/patches/${uuid}`);
    return res.json();
  }

  // Saves a full patch to the device.  Throws on failure so callers can
  // roll back optimistic editor state.
  async savePatch(uuid, data) {
    await this._fetch('POST', `/patches/${uuid}`, data);
  }

  // Returns the full device config: { settings, patches }.
  async getConfig() {
    const res = await this._fetch('GET', '/config');
    return res.json();
  }

  // Replaces the full device config.  Throws on failure so callers can
  // roll back optimistic editor state.
  async saveConfig(data) {
    await this._fetch('POST', '/config', data);
  }

  // Returns the current WiFi status from the device, or null on older firmware
  // that pre-dates GET /wifi-status.
  async getWifiStatus() {
    try {
      const res = await this._fetch('GET', '/wifi-status');
      return res.json();
    } catch {
      return null;
    }
  }

  // Returns the capabilities object from the device.  Falls back to empty
  // capabilities for firmware that pre-dates GET /capabilities, or on error.
  async getCapabilities() {
    try {
      const res = await this._fetch('GET', '/capabilities');
      const parsed = await res.json();
      if (parsed.capabilities_version !== 1) return { capabilities_version: 1, capabilities: {} };
      return parsed;
    } catch {
      return { capabilities_version: 1, capabilities: {} };
    }
  }

  // ---- OTA firmware update over WiFi (MDL-149) ----

  // Runs a complete firmware update over the /ota/* HTTP endpoints: begin →
  // per-file transfer → verify → commit. Emits the same onProgress stages as
  // CobsConnection.runFirmwareUpdate so FirmwareUpdateModal is transport-
  // agnostic. Aborts the session and re-throws on any error, leaving the
  // device on its existing firmware.
  //
  // manifest: { version, files: [{ path, data: Uint8Array, sha256? }] }
  //   version.json must be present and is always transferred last.
  async runFirmwareUpdate(manifest, onProgress) {
    onProgress?.({ stage: 'begin' });
    const { session, partition } = await this._otaBegin(manifest.version);
    onProgress?.({ stage: 'ready', partition });

    try {
      // version.json must always be the last file transferred: the device
      // treats its presence as the signal that the payload is complete.
      const versionFile = manifest.files.find(f => f.path === 'version.json');
      const otherFiles  = manifest.files.filter(f => f.path !== 'version.json');
      const orderedFiles = versionFile ? [...otherFiles, versionFile] : otherFiles;

      for (let i = 0; i < orderedFiles.length; i++) {
        const file = orderedFiles[i];
        onProgress?.({ stage: 'transferring', file: file.path, fileIndex: i, totalFiles: orderedFiles.length });
        await this._otaSendFile(session, file.path, file.data, file.sha256 ?? null);
      }

      onProgress?.({ stage: 'verifying' });
      const verifiedVersion = await this._otaVerify(session);
      onProgress?.({ stage: 'verified', version: verifiedVersion });

      onProgress?.({ stage: 'committing' });
      await this._otaCommit(session);
      onProgress?.({ stage: 'committed' });
    } catch (err) {
      // Best-effort abort so the device tears the half-written session down.
      // The abort itself may fail (device already rebooted, token rotated, or
      // network gone) — that must not mask the original error.
      try { await this._otaAbort(session); } catch { /* ignore */ }
      throw err;
    }
  }

  // POST an /ota/* request. `json` sends a JSON body; `raw` sends a Uint8Array
  // as application/octet-stream (used for chunks — no base64). Returns the
  // parsed JSON response. Throws HttpDeviceError on a non-OK status, carrying
  // the device's `reason` in both `.reason` and the message so OTA failures
  // surface clearly (401 during OTA also sets `.isUnauthorized`).
  async _otaPost(path, { json = null, raw = null } = {}) {
    const headers = { Authorization: `Bearer ${this.token}` };
    const opts = { method: 'POST', headers };
    if (raw !== null) {
      headers['Content-Type'] = 'application/octet-stream';
      opts.body = raw;
    } else if (json !== null) {
      headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(json);
    }

    const res = await fetch(`${this.baseUrl}${path}`, opts);
    let data = null;
    try { data = await res.json(); } catch { /* device may send no body */ }

    if (!res.ok) {
      const err = new HttpDeviceError(res.status);
      err.reason = data?.reason ?? null;
      if (err.reason) err.message = `${err.message} (${err.reason})`;
      throw err;
    }
    return data ?? {};
  }

  async _otaBegin(version) {
    const data = await this._otaPost('/ota/begin', { json: { version } });
    if (!data.session_id) {
      throw new Error(`OTA begin failed: ${JSON.stringify(data)}`);
    }
    return { session: data.session_id, partition: data.target_partition };
  }

  async _otaSendFile(session, path, data, sha256 = null) {
    const body = { path, size: data.length };
    if (sha256) body.sha256 = sha256;
    await this._otaPost(`/ota/file/begin?session=${session}`, { json: body });

    let seq = 0;
    let offset = 0;
    while (offset < data.length) {
      const chunk = data.subarray(offset, offset + OTA_CHUNK_SIZE);
      const res = await this._otaPost(
        `/ota/chunk?session=${session}&seq=${seq}`, { raw: chunk });
      seq = res.seq_next;      // device echoes the next expected seq
      offset += chunk.length;
    }

    await this._otaPost(`/ota/file/end?session=${session}`);
  }

  async _otaVerify(session) {
    const res = await this._otaPost(`/ota/verify?session=${session}`);
    return res.version;
  }

  async _otaCommit(session) {
    // The device flips the boot flag, responds, then reboots ~0.5 s later.
    await this._otaPost(`/ota/commit?session=${session}`);
  }

  async _otaAbort(session) {
    await this._otaPost(`/ota/abort?session=${session}`);
  }

  // Re-establish the session after the device reboots into new firmware.
  //
  // A reboot mints a fresh auth token, so the token captured at connect time is
  // now stale — every request (and the token-in-query WebSocket) would 401.
  //
  // Crucially, the device keeps serving HTTP for ~0.5s after /ota/commit
  // responds (the reboot is deferred so the response can flush), so the old
  // firmware is still up and will happily answer /auth and /version with the
  // OLD token and OLD version. Adopting that would leave the editor on a
  // soon-to-be-stale token showing the pre-update version. To avoid the race we
  // poll until the device reports the EXPECTED new version, and only then adopt
  // that iteration's token — which is guaranteed to be the new firmware's.
  //
  // Throws if the device does not come back on expectedVersion within
  // timeoutMs. Used by FirmwareUpdateModal after a WiFi OTA commit; USB has no
  // token to rotate and uses a plain reboot wait instead.
  async reconnectAfterReboot(expectedVersion, { timeoutMs = 60000, pollIntervalMs = 1000 } = {}) {
    const deadline = Date.now() + timeoutMs;
    const want = expectedVersion ? Version.parse(expectedVersion).toString() : null;

    while (Date.now() < deadline) {
      try {
        // 1. Fetch a fresh token (unauthenticated). This may still be the
        //    pre-reboot firmware, so it is not adopted until step 2 confirms
        //    the version.
        const authRes = await fetch(`${this.baseUrl}/auth`, { signal: AbortSignal.timeout(3000) });
        if (authRes.ok) {
          const { token } = await authRes.json();
          if (token) {
            // 2. Ask which firmware answered, using the token we just got.
            const verRes = await fetch(`${this.baseUrl}/version`, {
              headers: { Authorization: `Bearer ${token}` },
              signal: AbortSignal.timeout(3000),
            });
            if (verRes.ok) {
              const { version } = await verRes.json();
              const reported = version ? Version.parse(version).toString() : null;
              // Accept only once the new firmware is up (version matches). When
              // no expected version is supplied, accept the first valid answer.
              if (!want || reported === want) {
                this.token = token;
                if (reported) this.firmwareVersion = Version.parse(version);
                // Reopen the WebSocket with the fresh token (only if one was in
                // use — the old socket is retrying against the stale token).
                if (this._ws) this.openWebSocket();
                return this.firmwareVersion;
              }
            }
          }
        }
      } catch {
        // Device rebooting / WiFi not up yet — keep polling.
      }
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    throw new Error(
      `Device did not come back online on firmware ${want ?? '(any)'} after the update`);
  }
}
